/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import { useEffect, useState } from 'react';

/**
 * How far a dialog should be inset from the left so it centres on the WORK
 * AREA rather than the whole window.
 *
 * WHY. A dialog overlay covers the window, sidebar included, so centring it in
 * the viewport puts it half a sidebar left of where the reader is looking. It
 * measures as perfectly centred and still reads as misplaced, because the eye
 * takes the content pane as the page and the sidebar as furniture.
 *
 * MEASURED, NOT ASSUMED. The sidebar collapses, and its two widths are declared
 * in three places that already disagree (`--sidebar-collapsed-width: 70px`
 * against an inline `80`). Reading the rendered element is right in both states
 * and cannot drift from whichever number wins.
 *
 * Returns 0 when there is no sidebar — a narrow screen, or a page outside the
 * dashboard shell — so the dialog simply centres on the window, which is
 * correct there.
 */
function measure() {
  if (typeof document === 'undefined') return 0;
  const sidebar = document.querySelector('.sidebar');
  if (!sidebar) return 0;
  const { width } = sidebar.getBoundingClientRect();
  // A sidebar wider than a third of the window is not furniture beside the
  // content, it IS the layout (mobile drawer). Insetting there would squeeze
  // the dialog into a sliver.
  if (width >= window.innerWidth / 3) return 0;
  return Math.round(width);
}

/**
 * The inset, kept CURRENT while the dialog is open.
 *
 * Measuring once on open is not enough, and the failure is not subtle: collapse
 * the sidebar (or resize the window) with the dialog open and it keeps padding
 * by the old width, stranding the dialog against one edge. The sidebar animates
 * between 260 and 80, so a resize listener alone would also settle on a
 * mid-animation width — hence observing the element itself, which reports every
 * step and lands on the final value.
 */
export function useDialogInset() {
  const [inset, setInset] = useState(measure);

  useEffect(() => {
    const update = () => setInset(measure());
    update();

    window.addEventListener('resize', update);

    // The sidebar's width changes without the window resizing, so the element
    // has to be watched directly.
    const sidebar = document.querySelector('.sidebar');
    let observer;
    if (sidebar && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(sidebar);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
    };
  }, []);

  return inset;
}

export default measure;
